import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import { useMemo } from "react";
import { OptionTemplate } from "shared/api/middleware-api-client";

import { useAnalytics } from "app/hooks/shared/analytics/useAnalytics";
const GLASS_CATEGORIES: GlassCategory[] = [
  {
    title: "Doorzichtig",
    description: "Kies voor een transparante glassoort voor extra doorkijk.",
    codes: ["helder", "geluidswerkend", "rook", "bruinrook"],
  },
  {
    title: "Niet doorzichtig",
    description:
      "Voor wanneer je de ruimte echt wilt afsluiten of meer privacy wilt creëren.",
    codes: ["mat", "rookmat", "bruinmat"],
    receivesColorMatchedGlass: true,
  },
  // Last because these sit at a noticeably higher price point than the rest
  {
    title: "Figuurglas",
    description: "Een unieke glassoort met het patroon van linnen stof.",
    codes: ["linnenwit", "linnengrijs", "linnenbrons"],
  },
];

interface GlassCategory {
  title: string;
  description: string;
  codes: string[];
  /**
   * The aluminium plate that follows the chosen door colour is appended here.
   * Marked explicitly rather than assuming the last category, so the order of
   * GLASS_CATEGORIES can change without the plate ending up under Figuurglas.
   */
  receivesColorMatchedGlass?: boolean;
}

export function useGlassHelper({
  isBusinessDoor = false,
}: { isBusinessDoor?: boolean } = {}) {
  const {
    doorOptions,
    doorOptionsLoading,
    doorOptionsLoadError,
    updateGlass,
    color,
    selectedDoor,
    getConfigurationProduct,
  } = useConfigurationOptionsContext();
  const { onConfig } = useAnalytics();
  const glassCategories = useMemo(() => {
    if (!doorOptions || !doorOptions.glass) return [];
    // Use destructuring to get the glass property
    const { glass } = doorOptions;

    // Filter and map the glass categories based on the available glass options
    const glassTypes = GLASS_CATEGORIES.map((category) => {
      const items = category.codes
        //Only show geluidswerkend glass for business doors
        .filter((code) => {
          if (!isBusinessDoor && code === "geluidswerkend") {
            return false;
          }
          return true;
        })
        .map((code) => glass.find((glassItem) => glassItem.code === code))
        // A code the API did not return for this door would otherwise become an
        // undefined entry and crash GlassCard on glass.code
        .filter((glassItem): glassItem is OptionTemplate => Boolean(glassItem));

      return {
        ...category,
        items,
      };
    });

    const matchingGlass = glass.find(
      (glassItem) => glassItem.material?.codeName === color?.material?.codeName,
    ) as OptionTemplate;

    // Add the matching glass item to the category that takes it, if it exists
    if (matchingGlass) {
      const colorMatchedCategory = glassTypes.find(
        (category) => category.receivesColorMatchedGlass,
      );
      if (colorMatchedCategory) {
        colorMatchedCategory.items.push(matchingGlass);
      }
    }

    // Don't render a heading for a category this door has no glass for
    return glassTypes.filter((category) => category.items.length > 0);
  }, [doorOptions, color?.material?.codeName, isBusinessDoor]);

  const onUpdateGlass = (glass: OptionTemplate) => {
    updateGlass(glass);

    const product = getConfigurationProduct();
    if (product)
      onConfig({
        ...product,
        glassType: {
          id: glass.id,
          name: glass.name,
          code: glass.code,
          description: glass.description,
          imageId: glass.imageId,
        },
      });
  };
  return {
    doorOptionsLoading,
    doorOptionsLoadError,
    doorOptions,
    selectedDoor,
    color,
    glassCategories,
    onUpdateGlass,
  };
}
